import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import PersonalInformationFormFields from '@/features/onboarding/personal-information/components/fields';
import { useIsPersonalInformationFieldLocked } from '@/features/onboarding/personal-information/hooks/use-is-field-locked';
import type { PersonalInformationHomeAddress } from '@/features/onboarding/personal-information/schema';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';

export const PersonalInformationFormFieldsetHomeAddressLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('personalInformation.homeAddressTitle')}</PageHeadingTitle>
        <PageHeadingDescription>{t('personalInformation.homeAddressDescription')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
PersonalInformationFormFieldsetHomeAddressLegend.displayName = 'PersonalInformationFormFieldsetHomeAddressLegend';

export const PersonalInformationFormFieldsetHomeAddress = () => {
  const form = useFormContext<PersonalInformationHomeAddress>();
  const isLocked = useIsPersonalInformationFieldLocked();
  const { data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const countryOfResidency = form.watch('countryOfResidency');
  const postalCodeUse = (countryOfResidency && countries?.[countryOfResidency]?.postalCodeUse) || 'required';

  return (
    <PersonalInformationFormFields>
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('personalInformation.address')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={isLocked('address')} placeholder={t('personalInformation.placeholders.address')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address2"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('personalInformation.address2')}</FormLabel>
            <FormControl>
              <Input {...field} disabled={isLocked('address2')} placeholder={t('personalInformation.placeholders.address2')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {postalCodeUse !== 'none' && (
        <FormField
          control={form.control}
          name="postCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('personalInformation.postCode')}
                {postalCodeUse === 'required' ? '*' : ''}
              </FormLabel>
              <FormControl>
                <Input {...field} disabled={isLocked('postCode')} placeholder={t('personalInformation.placeholders.postCode')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem className={postalCodeUse === 'none' ? 'col-span-2' : undefined}>
            <FormLabel>{t('personalInformation.city')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={isLocked('city')} placeholder={t('personalInformation.placeholders.city')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </PersonalInformationFormFields>
  );
};
PersonalInformationFormFieldsetHomeAddress.displayName = 'PersonalInformationFormFieldsetHomeAddress';
